import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Title, Text, TextInput, Select, Textarea, Button, Group, Stack,
  Paper, Grid, Divider, Badge, Alert, Tooltip
} from '@mantine/core';
import { IconDeviceFloppy, IconMail, IconInfoCircle } from '@tabler/icons-react';
import { isMinor as checkIsMinor } from '../../services/registrationService';
import { getAgeDivision, carryOverDetails } from '../../services/schoolService';
import PhoneInputField from '../PhoneInputField/PhoneInputField';
import { useAutoSave } from '../../hooks/useAutoSave';
import StudentVariationSection from './StudentVariationSection';

const inputStyles = { label: { textAlign: 'left' }, description: { textAlign: 'left' } };

const GENDER_OPTIONS = [
  { value: 'Female', label: 'Female' },
  { value: 'Male', label: 'Male' },
  { value: 'Non-binary', label: 'Non-binary' },
  { value: 'Prefer not to say', label: 'Prefer not to say' }
];

const EXPERIENCE_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: '1-2 competitions', label: '1-2 competitions' },
  { value: '3-5 competitions', label: '3-5 competitions' },
  { value: '5+ competitions', label: '5+ competitions' }
];

const StudentForm = forwardRef(({
  registrationId,
  initialData,
  initialVariations, // undefined while still loading
  school,
  isFinalized = false,
  consentStatus = 'not_required',
  onSave,
  onNameChange,
  onParentConsentNeeded,
  saveVariationsFn,
  ensembleNames = [],
  pastStudents = []
}, ref) => {
  const readOnly = isFinalized;

  const buildFormData = (di) => ({
    first_name: di?.first_name || '',
    last_name: di?.last_name || '',
    name_pronunciation: di?.name_pronunciation || '',
    date_of_birth: di?.date_of_birth || '',
    gender: di?.gender || '',
    email: di?.email || '',
    phone_number: di?.phone_number || '',
    address_street: di?.address_street || '',
    address_city: di?.address_city || '',
    address_province: di?.address_province || '',
    address_postal_code: di?.address_postal_code || '',
    address_country: di?.address_country || 'Canada',
    parent_guardian_name: di?.parent_guardian_name || '',
    parent_guardian_email: di?.parent_guardian_email || '',
    emergency_contact_name: di?.emergency_contact_name || `${school?.contact_first_name || ''} ${school?.contact_last_name || ''}`.trim(),
    emergency_contact_relationship: di?.emergency_contact_relationship || 'Teacher / Studio Director',
    emergency_contact_phone: di?.emergency_contact_phone || school?.contact_phone || '',
    emergency_contact_email: di?.emergency_contact_email || school?.contact_email || '',
    medical_conditions: di?.medical_conditions || '',
    special_accommodations: di?.special_accommodations || '',
    years_training: di?.years_training ? String(di.years_training) : '',
    current_level: di?.current_level || '',
    competition_experience: di?.competition_experience || ''
  });

  const [formData, setFormData] = useState(() => buildFormData(initialData));
  const [isUnder18, setIsUnder18] = useState(false);
  const [ageDivision, setAgeDivision] = useState('');
  const [errors, setErrors] = useState({});
  // null until the solo section reports a change; then the full list (possibly
  // empty), so removing a student's last solo is saved too.
  const [pendingVariations, setPendingVariations] = useState(null);
  const [isSavingManual, setIsSavingManual] = useState(false);
  const [isSendingConsent, setIsSendingConsent] = useState(false);

  useEffect(() => {
    setFormData(buildFormData(initialData));
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (formData.date_of_birth) {
      setIsUnder18(checkIsMinor(formData.date_of_birth));
      setAgeDivision(getAgeDivision(formData.date_of_birth));
    } else {
      setIsUnder18(false);
      setAgeDivision('');
    }
  }, [formData.date_of_birth]);

  // The `required` props on these inputs were decorative — nothing ever wrote
  // to `errors`. That is why 15 of 21 students finished 2026 with no parent
  // email on file. This runs on every save.
  const validateStudent = useCallback((data, minor) => {
    const next = {};
    if (!data.first_name?.trim()) next.first_name = 'Required';
    if (!data.last_name?.trim()) next.last_name = 'Required';
    if (!data.date_of_birth) next.date_of_birth = 'Required';
    if (minor) {
      if (!data.parent_guardian_name?.trim()) next.parent_guardian_name = 'Required for students under 18';
      if (!data.parent_guardian_email?.trim()) next.parent_guardian_email = 'Required for students under 18';
    }
    return next;
  }, []);

  // Typed a name that was on an earlier roster? Offer to copy their details
  // (until a birthday is entered, or the hint is used or dismissed).
  const [carryOverDismissed, setCarryOverDismissed] = useState(false);
  const typedName = `${formData.first_name} ${formData.last_name}`.trim().toLowerCase().replace(/\s+/g, ' ');
  const nameMatch = !readOnly && !carryOverDismissed && !formData.date_of_birth && formData.first_name.trim() && formData.last_name.trim()
    ? pastStudents.find(p => `${p.dancerInfo.first_name} ${p.dancerInfo.last_name}`.trim().toLowerCase().replace(/\s+/g, ' ') === typedName)
    : null;
  const applyCarryOver = () => {
    setFormData(prev => ({ ...prev, ...carryOverDetails(nameMatch.dancerInfo) }));
    setCarryOverDismissed(true);
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if ((field === 'first_name' || field === 'last_name') && onNameChange) {
        onNameChange(registrationId, next.first_name, next.last_name);
      }
      return next;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  // Must respect readOnly. useAutoSave fires on a timer AND on unmount (its
  // saved-data ref starts null, so the first mount always counts as changed),
  // so without this a teacher merely *viewing* a finalized or closed-season
  // roster writes to it — including recomputing parent_consent_status.
  const canAutoSave = !!registrationId && !readOnly;

  const performSave = useCallback(async (data) => {
    if (!registrationId || !onSave) return;
    // Surfaced, not enforced: a half-typed student must still be saved, or
    // autosave would throw away the teacher's work. Finalize is the gate.
    setErrors(validateStudent(data, checkIsMinor(data.date_of_birth)));
    const payload = {
      ...data,
      date_of_birth: data.date_of_birth || null,
      years_training: data.years_training ? parseInt(data.years_training, 10) : null
    };
    await onSave(registrationId, payload);

    // Save solos (deletes any no longer listed)
    if (pendingVariations && saveVariationsFn) {
      await saveVariationsFn(registrationId, pendingVariations);
    }
  }, [registrationId, onSave, pendingVariations, saveVariationsFn, validateStudent]);

  const { lastSaved, isSaving } = useAutoSave(formData, performSave, 30000, canAutoSave);

  useImperativeHandle(ref, () => ({
    flush: async () => {
      await performSave(formData);
    }
  }), [performSave, formData]);

  const handleManualSave = async () => {
    setIsSavingManual(true);
    await performSave(formData);
    setIsSavingManual(false);
  };

  const handleSendConsent = async () => {
    if (!formData.parent_guardian_email || !onParentConsentNeeded) return;
    setIsSendingConsent(true);
    await onParentConsentNeeded(registrationId, formData.parent_guardian_email);
    setIsSendingConsent(false);
  };

  const saving = isSaving || isSavingManual;

  const studentName = formData.first_name || formData.last_name
    ? `${formData.first_name} ${formData.last_name}`.trim()
    : 'New Student';

  return (
    <div style={{ padding: '1.5rem', overflowY: 'auto', height: '100%' }}>
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={4} style={{ color: 'var(--charcoal)' }}>{studentName}</Title>
          {ageDivision && <Badge variant="light" color="gold" mt={4}>{ageDivision}</Badge>}
        </div>
        {!readOnly && (
          <Group>
            {lastSaved && <Text size="xs" color="dimmed">Saved {lastSaved instanceof Date ? lastSaved.toLocaleTimeString() : lastSaved}</Text>}
            {saving && <Text size="xs" color="dimmed">Saving...</Text>}
            <Button
              size="sm"
              variant="light"
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleManualSave}
              loading={isSavingManual}
              style={{ color: 'var(--primary-gold)', borderColor: 'var(--primary-gold)' }}
            >
              Save
            </Button>
          </Group>
        )}
      </Group>

      <Stack spacing="xl">

        {nameMatch && (
          <Alert color="gold" variant="light">
            <Text size="sm" mb="xs" style={{ textAlign: 'left' }}>
              <strong>{nameMatch.dancerInfo.first_name} {nameMatch.dancerInfo.last_name}</strong> was on your {nameMatch.year} roster.
              Copy their birthday, contact, parent/guardian and emergency contact details?
            </Text>
            <Group gap="sm">
              <Button size="xs" color="gold" onClick={applyCarryOver}>Copy details</Button>
              <Button size="xs" variant="subtle" color="gray" onClick={() => setCarryOverDismissed(true)}>No, it's someone else</Button>
            </Group>
          </Alert>
        )}

        {/* Personal Info */}
        <Paper p="md" radius="md" withBorder>
          <Title order={6} mb="md" color="var(--soft-gray)">Personal Information</Title>
          <Grid gutter="sm">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="First Name" placeholder="Jane" required value={formData.first_name} onChange={(e) => handleChange('first_name', e.target.value)} error={errors.first_name} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Last Name" placeholder="Smith" required value={formData.last_name} onChange={(e) => handleChange('last_name', e.target.value)} error={errors.last_name} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Name Pronunciation" placeholder='e.g. "Aoife (Ee-fah)"' value={formData.name_pronunciation} onChange={(e) => handleChange('name_pronunciation', e.target.value)} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Date of Birth" type="date" required value={formData.date_of_birth} onChange={(e) => handleChange('date_of_birth', e.target.value)} error={errors.date_of_birth} styles={inputStyles} disabled={readOnly} max={new Date().toISOString().split('T')[0]} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select label="Gender" placeholder="Select" data={GENDER_OPTIONS} value={formData.gender} onChange={(v) => handleChange('gender', v)} styles={inputStyles} disabled={readOnly} clearable />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label={
                  <Group gap={4} wrap="nowrap" component="span">
                    Student Contact Email (optional)
                    <Tooltip
                      multiline
                      w={260}
                      withArrow
                      label="If the student or their parent/guardian would also like to receive competition updates (schedule, reminders), add their email here. Your studio still receives everything."
                    >
                      <IconInfoCircle size={15} style={{ color: 'var(--soft-gray)', cursor: 'help' }} />
                    </Tooltip>
                  </Group>
                }
                placeholder="jane@example.com"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                styles={inputStyles}
                disabled={readOnly}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <PhoneInputField label="Phone Number" value={formData.phone_number} onChange={(v) => handleChange('phone_number', v)} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
          </Grid>

          <Divider my="md" label="Address (optional)" labelPosition="left" />
          <Grid gutter="sm">
            <Grid.Col span={12}>
              <TextInput label="Street Address" value={formData.address_street} onChange={(e) => handleChange('address_street', e.target.value)} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput label="City" value={formData.address_city} onChange={(e) => handleChange('address_city', e.target.value)} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput label="Province" value={formData.address_province} onChange={(e) => handleChange('address_province', e.target.value)} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput label="Postal Code" value={formData.address_postal_code} onChange={(e) => handleChange('address_postal_code', e.target.value)} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
          </Grid>
        </Paper>

        {/* Parent/Guardian — conditional on minor */}
        {isUnder18 && (
          <Paper p="md" radius="md" withBorder style={{ borderLeft: '3px solid var(--primary-gold)' }}>
            <Title order={6} mb="xs" color="var(--soft-gray)">Parent / Guardian</Title>
            <Grid gutter="sm" mb="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Parent / Guardian Name" placeholder="John Smith" required value={formData.parent_guardian_name} onChange={(e) => handleChange('parent_guardian_name', e.target.value)} error={errors.parent_guardian_name} styles={inputStyles} disabled={readOnly} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Parent / Guardian Email" placeholder="parent@example.com" type="email" required value={formData.parent_guardian_email} onChange={(e) => handleChange('parent_guardian_email', e.target.value)} error={errors.parent_guardian_email} styles={inputStyles} disabled={readOnly} />
              </Grid.Col>
            </Grid>
            {!readOnly && (
              <Group>
                <Button
                  size="sm"
                  variant="light"
                  color="yellow"
                  leftSection={<IconMail size={15} />}
                  onClick={handleSendConsent}
                  loading={isSendingConsent}
                  disabled={!formData.parent_guardian_email || consentStatus === 'received'}
                >
                  {consentStatus === 'received' ? 'Consent Received' : consentStatus === 'pending' ? 'Resend Consent Email' : 'Send Consent Email'}
                </Button>
                {consentStatus === 'pending' && (
                  <Text size="xs" color="dimmed">Consent email sent — waiting for parent.</Text>
                )}
              </Group>
            )}
          </Paper>
        )}

        {/* Emergency Contact */}
        <Paper p="md" radius="md" withBorder>
          <Title order={6} mb="xs" color="var(--soft-gray)">Emergency Contact</Title>
          <Text size="xs" color="dimmed" mb="md">Pre-filled with teacher info. Edit if the student has a different emergency contact.</Text>
          <Grid gutter="sm">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Name" required value={formData.emergency_contact_name} onChange={(e) => handleChange('emergency_contact_name', e.target.value)} error={errors.emergency_contact_name} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Relationship" required value={formData.emergency_contact_relationship} onChange={(e) => handleChange('emergency_contact_relationship', e.target.value)} error={errors.emergency_contact_relationship} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <PhoneInputField label="Phone" required value={formData.emergency_contact_phone} onChange={(v) => handleChange('emergency_contact_phone', v)} error={errors.emergency_contact_phone} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Email" type="email" value={formData.emergency_contact_email} onChange={(e) => handleChange('emergency_contact_email', e.target.value)} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
          </Grid>
        </Paper>

        {/* Medical */}
        <Paper p="md" radius="md" withBorder>
          <Title order={6} mb="md" color="var(--soft-gray)">Medical Information</Title>
          <Stack spacing="sm">
            <Textarea label="Medical Conditions / Allergies" placeholder="List any allergies, medications, or conditions the organizers should be aware of" value={formData.medical_conditions} onChange={(e) => handleChange('medical_conditions', e.target.value)} styles={inputStyles} minRows={2} disabled={readOnly} />
            <Textarea label="Special Accommodations" placeholder="Any accessibility needs or special requirements" value={formData.special_accommodations} onChange={(e) => handleChange('special_accommodations', e.target.value)} styles={inputStyles} minRows={2} disabled={readOnly} />
          </Stack>
        </Paper>

        {/* Dance Background */}
        <Paper p="md" radius="md" withBorder>
          <Title order={6} mb="md" color="var(--soft-gray)">Dance Background</Title>
          <Grid gutter="sm">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput label="Years Training" type="number" min={0} placeholder="e.g. 5" value={formData.years_training} onChange={(e) => handleChange('years_training', e.target.value)} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput label="Current Level" placeholder="e.g. Advanced 1, RAD Grade 7" value={formData.current_level} onChange={(e) => handleChange('current_level', e.target.value)} styles={inputStyles} disabled={readOnly} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Select label="Competition Experience" data={EXPERIENCE_OPTIONS} value={formData.competition_experience} onChange={(v) => handleChange('competition_experience', v)} styles={inputStyles} disabled={readOnly} clearable />
            </Grid.Col>
          </Grid>
        </Paper>

        {/* Solos */}
        <StudentVariationSection
          registrationId={registrationId}
          ageDivision={ageDivision}
          dateOfBirth={formData.date_of_birth}
          initialVariations={initialVariations}
          onVariationsChange={setPendingVariations}
          readOnly={readOnly}
        />

        {/* Ensembles: read-only here, edited on the Ensembles step */}
        <Paper p="md" radius="md" withBorder style={{ background: '#fafafa' }}>
          <Text size="sm" style={{ textAlign: 'left' }}>
            <strong>Ensembles:</strong>{' '}
            {ensembleNames.length ? ensembleNames.join(', ') : 'none'}
            <Text component="span" size="xs" color="dimmed"> — add or edit these on the Ensembles step.</Text>
          </Text>
        </Paper>

      </Stack>
    </div>
  );
});

export default StudentForm;
